// public/js/features/pois/poi-overlay-layer.js
// Custom map-image overlays attached to POIs (docs/poi-custom-maps.md). Rendered
// as MapLibre `image` sources + `raster` layers ONE LEVEL ABOVE THE BASE MAP and
// BELOW everything else (POI pins, geofences, assets, routes…). Shared by both
// the main map and the detail fullscreen map.
//
// Layer id conventions: source `poi-ov-src-<overlayId>`, layer `poi-ov-<overlayId>`.
import { poiState } from './poi-state.js';
import { getCurrentUser } from '../../modules/rbac.js';

const VISIBLE_KEY = 'poi_overlays_visible'; // localStorage '1' | '0' (global toggle)
const srcId = (id) => `poi-ov-src-${id}`;
const layerId = (id) => `poi-ov-${id}`;
const isOverlayLayer = (l) => typeof l === 'string' && l.startsWith('poi-ov-');

// Layers OUR code adds on top of the base map. The overlay must sit ABOVE all
// base-map layers (raster tiles OR a vector style's background/roads/labels) but
// BELOW these, so pins, geofences, clusters, routes and tether lines stay on top.
const APP_LAYER_PREFIXES = [
  'poi-ov-', 'poi-symbols', 'poi-geofence', 'poi-draw',
  'clusters', 'cluster-count', 'unclustered',
  'tether-', 'history-route', 'accuracy', 'asset-', 'route-',
];
const isAppLayer = (id) => APP_LAYER_PREFIXES.some(p => id.startsWith(p));

// ── per-user opacity (client-side, individual, persistent) ────────────────────
function opKey(overlayId) {
  const uid = (getCurrentUser()?.sub || getCurrentUser()?.username || 'anon');
  return `poi_ov_op:${uid}:${overlayId}`;
}
export function overlayOpacity(overlay) {
  const raw = localStorage.getItem(opKey(overlay.id));
  const v = raw == null ? overlay.opacityDefault : Number(raw);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7;
}
export function setOverlayOpacity(map, overlay, value) {
  const v = Math.min(1, Math.max(0, Number(value) || 0));
  localStorage.setItem(opKey(overlay.id), String(v));
  try { if (map?.getLayer(layerId(overlay.id))) map.setPaintProperty(layerId(overlay.id), 'raster-opacity', v); } catch (e) {}
}

export function overlaysVisible() { return (localStorage.getItem(VISIBLE_KEY) ?? '1') === '1'; }
export function setOverlaysVisible(map, visible) {
  localStorage.setItem(VISIBLE_KEY, visible ? '1' : '0');
  const vis = visible ? 'visible' : 'none';
  (map?.getStyle?.().layers || []).forEach(l => {
    if (isOverlayLayer(l.id)) { try { map.setLayoutProperty(l.id, 'visibility', vis); } catch (e) {} }
  });
}

// ── affine georeferencing (pixel → lng/lat), least squares over ≥3 points ─────
// Returns { a,b,c, d,e,f }: lng = a*px + b*py + c ; lat = d*px + e*py + f.
export function affineFromPoints(points) {
  if (!points || points.length < 3) return null;
  // Normal equations for basis [px, py, 1].
  const M = [[0,0,0],[0,0,0],[0,0,0]];
  const bx = [0,0,0], by = [0,0,0];
  for (const p of points) {
    const r = [Number(p.px), Number(p.py), 1];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) M[i][j] += r[i] * r[j];
      bx[i] += r[i] * Number(p.lng);
      by[i] += r[i] * Number(p.lat);
    }
  }
  const cx = solve3(M.map(row => row.slice()), bx.slice());
  const cy = solve3(M.map(row => row.slice()), by.slice());
  if (!cx || !cy) return null;
  return { a: cx[0], b: cx[1], c: cx[2], d: cy[0], e: cy[1], f: cy[2] };
}
export function applyAffine(af, px, py) {
  return [af.a * px + af.b * py + af.c, af.d * px + af.e * py + af.f];
}
// The four geo corners for MapLibre's image source: [TL, TR, BR, BL].
export function cornersFromAffine(af, width, height) {
  return [
    applyAffine(af, 0, 0),
    applyAffine(af, width, 0),
    applyAffine(af, width, height),
    applyAffine(af, 0, height),
  ];
}
// Solve a 3×3 system by Gaussian elimination with partial pivoting.
function solve3(A, b) {
  for (let i = 0; i < 3; i++) {
    let piv = i;
    for (let r = i + 1; r < 3; r++) if (Math.abs(A[r][i]) > Math.abs(A[piv][i])) piv = r;
    if (Math.abs(A[piv][i]) < 1e-12) return null;
    [A[i], A[piv]] = [A[piv], A[i]]; [b[i], b[piv]] = [b[piv], b[i]];
    for (let r = 0; r < 3; r++) {
      if (r === i) continue;
      const f = A[r][i] / A[i][i];
      for (let c = i; c < 3; c++) A[r][c] -= f * A[i][c];
      b[r] -= f * b[i];
    }
  }
  return [b[0] / A[0][0], b[1] / A[1][1], b[2] / A[2][2]];
}

// ── image loading (private serve → blob → DATA URL, cached by key) ────────────
// MapLibre's `image` source loads a data: URL reliably across browsers; blob:
// URLs are flaky in its image loader, so we inline the bytes as a data URL.
const urlCache = new Map(); // imageKey → dataURL
export async function imageDataUrl(imageKey) {
  if (urlCache.has(imageKey)) return urlCache.get(imageKey);
  const res = await window.authenticatedFetch(`api/v1/poi-overlay-image?key=${encodeURIComponent(imageKey)}`);
  if (!res.ok) throw new Error(`overlay image ${res.status}`);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('image read failed'));
    r.readAsDataURL(blob);
  });
  urlCache.set(imageKey, dataUrl);
  return dataUrl;
}

// Every overlay across the POIs the user can see (independent of the pin type
// filter, so an airport map stays even when its pin type is toggled off).
function allOverlays() {
  const out = [];
  for (const p of (poiState.pois || [])) {
    for (const o of (p.overlays || [])) {
      if (o && o.id && o.imageKey && Array.isArray(o.corners) && o.corners.length === 4) out.push(o);
    }
  }
  return out;
}

// Position every overlay layer ABOVE the whole base map but BELOW our app layers.
// Works for both a raster base and a vector style (whose opaque `background` +
// road/label layers are NOT "app" layers, so the overlay goes above them).
export function ensureOverlaysAtBottom(map) {
  if (!map || !map.getStyle) return;
  const layers = map.getStyle().layers || [];
  // The lowest app layer that isn't an overlay = the ceiling for the overlay.
  const firstApp = layers.find(l => !isOverlayLayer(l.id) && isAppLayer(l.id));
  for (const l of layers) {
    if (!isOverlayLayer(l.id)) continue;
    // Just below the first app layer; if none exist yet, to the very top
    // (above the entire base map) so it is never hidden by an opaque base layer.
    try { firstApp ? map.moveLayer(l.id, firstApp.id) : map.moveLayer(l.id); } catch (e) {}
  }
}

// Add/update overlay layers for the current POIs; remove stale ones. Async
// because images are fetched privately. Call from the POI render pipeline.
export async function renderPoiOverlays(map) {
  if (!map || typeof maplibregl === 'undefined') return;
  // Proceed as soon as the style is ready — not on full 'idle' (see core.js).
  if (!map.__styleReady && !map.isStyleLoaded()) { map.once('style.load', () => renderPoiOverlays(map)); return; }

  const overlays = allOverlays();
  const wanted = new Set(overlays.map(o => layerId(o.id)));
  const vis = overlaysVisible() ? 'visible' : 'none';

  // Remove overlay layers/sources no longer wanted.
  for (const l of (map.getStyle().layers || [])) {
    if (isOverlayLayer(l.id) && !wanted.has(l.id)) {
      try { map.removeLayer(l.id); } catch (e) {}
      const sid = l.id.replace('poi-ov-', 'poi-ov-src-');
      try { if (map.getSource(sid)) map.removeSource(sid); } catch (e) {}
    }
  }

  for (const o of overlays) {
    const lid = layerId(o.id), sid = srcId(o.id);
    const coords = o.corners.map(c => [Number(c[0]), Number(c[1])]);
    try {
      const existing = map.getSource(sid);
      if (existing) {
        try { existing.setCoordinates(coords); } catch (e) {}
        try { map.setPaintProperty(lid, 'raster-opacity', overlayOpacity(o)); } catch (e) {}
        try { map.setLayoutProperty(lid, 'visibility', vis); } catch (e) {}
      } else {
        const url = await imageDataUrl(o.imageKey);
        if (map.getSource(sid)) continue; // added concurrently
        map.addSource(sid, { type: 'image', url, coordinates: coords });
        map.addLayer({
          id: lid, type: 'raster', source: sid,
          layout: { visibility: vis },
          paint: { 'raster-opacity': overlayOpacity(o), 'raster-fade-duration': 0 },
        });
        console.log('[poi-overlay] added', o.id, 'opacity', overlayOpacity(o), 'vis', vis, 'coords', coords);
      }
    } catch (e) { console.warn('[poi-overlay] render failed', o.id, e?.message, e); }
  }

  ensureOverlaysAtBottom(map);
  console.log('[poi-overlay] layer order:', (map.getStyle().layers || []).map(l => l.id).join(' > '));
}

export function clearPoiOverlays(map) {
  if (!map || !map.getStyle) return;
  for (const l of (map.getStyle().layers || [])) {
    if (isOverlayLayer(l.id)) {
      try { map.removeLayer(l.id); } catch (e) {}
      const sid = l.id.replace('poi-ov-', 'poi-ov-src-');
      try { if (map.getSource(sid)) map.removeSource(sid); } catch (e) {}
    }
  }
}
